<script setup lang="ts">
import type { JournalListItemDTO } from '~/shared/api/client/journals/journals.dto';
import {
  JOURNAL_ORDER_ERRORS,
  JOURNAL_ORDER_FIELDS,
  JOURNAL_ORDER_PAYMENT_NOTE,
  PRIVACY_POLICY_URL,
} from '../lib/journalOrder.const';
import { useJournalOrderForm } from '../lib/useJournalOrderForm';
import JournalOrderField from './JournalOrderField.vue';
import JournalOrderCaptcha from './JournalOrderCaptcha.vue';

const props = defineProps<{
  issue: JournalListItemDTO;
}>();

const emit = defineEmits<{
  sent: [];
}>();

const { values, error, isSubmitting, isInvalid, setValue, submit } =
  useJournalOrderForm(() => props.issue);

const captcha = useTemplateRef('captcha');
const captchaToken = ref('');
const isConsentGiven = ref(false);

watch(captchaToken, (token) => {
  if (token && error.value === JOURNAL_ORDER_ERRORS.captcha) error.value = null;
});

const handleSubmit = async () => {
  if (await submit(captchaToken.value)) {
    emit('sent');
  } else if (captchaToken.value) {
    // The token is single-use and may already be spent by the failed request
    captcha.value?.reset();
  }
};
</script>

<template>
  <!-- A form element for the Enter key; the shared button is a link and calls the same handler -->
  <form :class="$style.form" @submit.prevent="handleSubmit">
    <div :class="$style.fields">
      <JournalOrderField
        v-for="field in JOURNAL_ORDER_FIELDS"
        :key="field.name"
        :model-value="values[field.name]"
        :label="field.label"
        :required="field.required"
        :invalid="isInvalid(field.name)"
        @update:model-value="setValue(field.name, $event)"
      />
    </div>

    <p :class="$style.payment">{{ JOURNAL_ORDER_PAYMENT_NOTE }}</p>

    <JournalOrderCaptcha
      ref="captcha"
      v-model="captchaToken"
      :class="$style.captcha"
    />

    <!--
      Consent gates the send: it is asked for on the layout and nowhere in the
      spec, and it is not stored with the order (ADR 0001, decision 12)
    -->
    <Checkbox v-model="isConsentGiven" :class="$style.consent">
      Принимаю условия
      <a :href="PRIVACY_POLICY_URL" target="_blank" rel="noopener noreferrer">
        Политики по защите персональных данных
      </a>
      и даю согласие на обработку моих персональных данных
    </Checkbox>

    <p v-if="error" :class="$style.error">{{ error }}</p>

    <Button
      theme="primary"
      size="xl"
      :disabled="!isConsentGiven"
      :is-loading="isSubmitting"
      :class="$style.submit"
      @click="handleSubmit"
    >
      <span :class="$style.submitLabel">
        Отправить
        <Icon name="my-icon:i-arrow-right" :class="$style.icon" />
      </span>
    </Button>
  </form>
</template>

<style module lang="scss">
.form {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

// Two columns filled row by row, in the order the fields are declared
.fields {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--space-2-5x);
  width: 100%;
}

.payment {
  @include text_body1_bold;
  color: var(--color-black-1);
  margin: var(--space-3x) 0 0;
}

.captcha {
  margin-top: var(--space-2-5x);
}

.consent {
  margin-top: var(--space-2-5x);
}

.error {
  @include text_body2;
  color: var(--color-red-2);
  margin: var(--space-1-5x) 0 0;
}

.submit {
  margin-top: var(--space-2-5x);
}

.submitLabel {
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-1-5x);
  width: 100%;
  @include text_button1;
}

.icon {
  flex-shrink: 0;
  width: 20px;
  height: 20px;

  // The shared arrow ships with a hardcoded white fill — follow the button text
  path {
    fill: currentColor;
  }
}

@media (max-width: 899px) {
  .fields {
    grid-template-columns: minmax(0, 1fr);
    gap: var(--space-1-5x);
  }

  .payment {
    text-align: center;
    align-self: center;
  }

  // Full width at the bottom of the column, as the mobile layout draws it
  .submit {
    width: 100%;
  }
}
</style>
